
import { useNavigate, } from 'react-router-dom';
import { HttpStatusCode } from 'axios';
import { Formik, Form, FormikErrors } from 'formik';
import { useState } from 'react';
import { useTranslation } from "../../i18n/useTranslation";
import { toast } from 'react-toastify';
import styles from './fixcenter-career.module.scss';
import * as yup from 'yup';
import { Validator } from '../../shared/constants/validator';
import classNames from 'classnames';
import globalStyles from "../../global-styles.module.scss"
import { ContactUsFormViewModel } from '../fixcenter-support/contact-us-form-view.model';
import { uploadResumeHeader, htmlId, minHeightDragAndDropFile, maxWidthDragAndDropFile, uploadFileInfo, extensionFiles } from './fixcenter-career.service';
import { getLinkToContactSuccess } from '../fixcenter-support/fixcenter-support.service';
import DragAndDropFile from '../../shared/file-handler/drag-and-drop-file';
import { Utils } from '../../shared/constants/utilities.constants';

const CareerUploadResumeForm: React.FC = () => {
    const { t } = useTranslation('translation');
    const [resumeFile, setResumeFile] = useState<File>();
    const [motivationLetterFile, setMotivationLetterFile] = useState<File>();

    const navigate = useNavigate();
    const showError = (message: string) => toast.error(message);

    const getModelDto = (values: ContactUsFormViewModel): FormData => {
        const dto: FormData = new FormData();
        dto.append("Message", values.message);
        dto.append("Name", getTranslate('EMAIL_MESSAGE_1') as string);
        dto.append("JsonMessages", JSON.stringify([getTranslate('EMAIL_MESSAGE_2'), getTranslate('EMAIL_MESSAGE_3'), getTranslate('EMAIL_MESSAGE_4')]));
        dto.append("Title", 'Fix center (' +  (t('CAREER').toUpperCase() + ')'))
        if (resumeFile) {
            dto.append("Files", resumeFile, resumeFile.name);
        }
        if (motivationLetterFile) {
            dto.append("Files", motivationLetterFile, motivationLetterFile.name);
        }
        return dto;
    }

    const getTranslate = (code: string) => {
        return t('CAREER_ITEMS.' + code);
    }

    const isValid = (errors: FormikErrors<ContactUsFormViewModel>): boolean => {
        return resumeFileExist() && motivationLetterFileFileExist() && Object.keys(errors).length === 0;
    }

    const resumeFileExist = (): boolean => {
        return !Utils.isNullOrUndefined(resumeFile);
    }

    const motivationLetterFileFileExist = (): boolean => {
        return !Utils.isNullOrUndefined(motivationLetterFile);
    }
    
    const validationSchema = yup.object<ContactUsFormViewModel>().shape({
        message: yup.string().required().max(Validator.textAreaLengthLimit)
    });

    const displayFillRequiredData = (errors: FormikErrors<ContactUsFormViewModel>) => {
        if (!isValid(errors)) {
            showError(t('register.fillAllRequiredData'));
        }
    }

    const send = (values: ContactUsFormViewModel, errors: FormikErrors<ContactUsFormViewModel>): void | Promise<any> => {
        if (isValid(errors)) {
            uploadResumeHeader(getModelDto(values)).then((response) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    navigate(getLinkToContactSuccess());
                } else {
                    showError(t('commonErrors.SENDING_MESSAGE'));
                }
            }).catch(() => showError(t('commonErrors.SENDING_MESSAGE')));;
        }
    }

    const handleResumeFileChange = (files: File[]) => {
        if (files && files.length > 0) {
            setResumeFile(files[0]);
        }
    };

    const handleMotivationLetterFileChange = (files: File[]) => {
        if (files && files.length > 0) {
            setMotivationLetterFile(files[0]);
        }
    };
      
    return (
        <div className={styles.form_container}>
            <Formik 
                validateOnMount 
                initialValues={new ContactUsFormViewModel()}
                validationSchema={validationSchema}
                onSubmit={() => {}}
            >{({
                values, handleChange, errors, touched }) =>
                <Form
                    autoComplete='off'
                    spellCheck='false'
                    className={styles.form} 
                    onMouseEnter={() => { }} 
                    onSubmit={(e) => {
                        e.preventDefault();
                        send(values, errors);
                    }}>
                    <div className={globalStyles.flex_center_item}>   
                        <div className={styles.content}>
                            <label className={styles.main_header_text}>
                                {t("CAREER_ITEMS.HEADER_1")}
                            </label>

                            <div className={classNames(globalStyles.space_between_item, styles.form_row)}>
                                <label className={styles.header_text}>{t('RESUME')}</label>
                                <div className={classNames(globalStyles.ml_3)}>
                                    <DragAndDropFile
                                        handleFileChange={handleResumeFileChange}
                                        maxWidth={maxWidthDragAndDropFile}
                                        minHeight={minHeightDragAndDropFile}
                                        fileNameInput={null}
                                        htmlId={htmlId + '-resume'}
                                        isValid={resumeFileExist()}
                                        uploadFileInfo={uploadFileInfo}
                                        extensionFiles={extensionFiles}
                                    />
                                </div>
                            </div>

                            <div className={classNames(globalStyles.space_between_item, styles.form_row)}>
                                <label className={styles.header_text}>{t('MOTIVATION_LETTER')}</label>
                                <div className={classNames(globalStyles.ml_3)}>
                                    <DragAndDropFile 
                                        handleFileChange={handleMotivationLetterFileChange}
                                        maxWidth={maxWidthDragAndDropFile}
                                        minHeight={minHeightDragAndDropFile}
                                        fileNameInput={null}
                                        htmlId={htmlId + '-motivation-letter'}
                                        isValid={motivationLetterFileFileExist()}
                                        uploadFileInfo={uploadFileInfo}
                                        extensionFiles={extensionFiles}
                                    />
                                </div>
                            </div>
                            
                            <div className={classNames(globalStyles.space_between_item, styles.form_row)}>
                                <label className={styles.header_text}>{t('MESSAGE')}*</label>
                                <textarea
                                    ref={(e) => {
                                        if (e) {
                                            errors['message'] ? e.setCustomValidity(t(errors['message'])) :  e.setCustomValidity('');          
                                        }
                                    }}
                                    id = {`${htmlId}-message-textarea`}
                                    name="message"
                                    value={values.message}
                                    onChange={handleChange}
                                >
                                </textarea>
                            </div>

                            <div className={styles.button_content}>
                                <button
                                    className={styles.send_btn}
                                    type='submit'
                                    id = {`${htmlId}-submit-btn`}
                                    onClick={() => {
                                        displayFillRequiredData(errors)
                                    }}
                                >
                                <span>{t("SEND")}</span>  
                                </button>
                            </div>
                        </div>
                    </div>
                </Form >
                }
            </Formik >
        </div>
    );
};

export default CareerUploadResumeForm;